import { useEffect, useRef } from 'react';
import useQuoteFsc from './useQuoteFsc';

interface Args {
  /** Dray rate + charges the FSC % applies to (directquote_draywithcharges). */
  dray: string;
  fsctype: string;
  fsctariff: string;
  deliveryzone: string;
  fscrate: string;
  miles: string;
  /** Yearly volume (quoterevisions_volume). */
  volume: string;
  /** The current Est. Revenue field value (decides the initial write). */
  estRev: string;
  /** Off in read-only contexts — no fetch, no recompute. */
  enabled: boolean;
  /** Write the recomputed Est. Revenue (quoterevisions_estimatedrevenue). */
  onEstRev: (v: string) => void;
}

/** Estimated revenue is shown to 2dp (matching the rest of the quote's money
 *  fields). V1 kept 5dp; we round for display consistency. */
const fmtEstRev = (product: number): string => product.toFixed(2);

/**
 * DIRECT-quote estimated revenue (V1 quote.js updateestimatedrevenue):
 *   estRev = (drayWithCharges + fscAdjustment) × volume
 * The FSC adjustment comes from the shared {@link useQuoteFsc} resolver, so the
 * CURRENT / CUSTOMER / DISCOUNTED branches all match V1 exactly. A SEPARATE
 * useQuoteFsc instance is used here (the est-rev calc must keep working while
 * the Direct sub-tab — which owns its own instance for the rate field — is
 * unmounted); it never auto-fills the rate.
 *
 * Recompute timing mirrors V1: a saved (non-zero) Est. Revenue is preserved on
 * load and only recomputed once the user edits an input, while an empty/0 value
 * is auto-filled immediately. A manual Est. Revenue edit survives until the next
 * input change overwrites it (V1's onchange recompute).
 */
export default function useEstimatedRevenue(a: Args): { adjustment: number } {
  const fsc = useQuoteFsc({
    fsctype: a.fsctype,
    fsctariff: a.fsctariff,
    deliveryzone: a.deliveryzone,
    fscrate: a.fscrate,
    dray: a.dray,
    miles: a.miles,
    enabled: a.enabled,
    onFscRate: () => { /* est-rev instance never fills the rate field */ },
  });
  const adjustment = fsc.adjustment;

  const cb = useRef(a.onEstRev);
  cb.current = a.onEstRev;
  const estRevRef = useRef(a.estRev);
  estRevRef.current = a.estRev;

  // User-controlled inputs (excludes the async `adjustment` so its post-load
  // settle is not mistaken for an edit) + whether we own the field yet.
  const inputsKeyRef = useRef<string | null>(null);
  const autoRef = useRef(false);

  useEffect(() => {
    if (!a.enabled) return;
    const inputsKey = [a.dray, a.volume, a.fscrate, a.fsctype, a.fsctariff, a.deliveryzone, a.miles].join('|');
    if (inputsKeyRef.current === null) {
      inputsKeyRef.current = inputsKey;
      // Take over an empty/0 field immediately; otherwise preserve the saved value.
      autoRef.current = (parseFloat(estRevRef.current || '') || 0) === 0;
    } else if (inputsKey !== inputsKeyRef.current) {
      inputsKeyRef.current = inputsKey;
      autoRef.current = true; // a real edit — recompute from here on
    }
    if (!autoRef.current) return;

    const dray = parseFloat(a.dray || '') || 0;
    const volume = parseFloat(a.volume || '') || 0;
    const product = (dray + adjustment) * volume;
    if (!Number.isFinite(product)) return;
    const next = fmtEstRev(product);
    if (next !== (estRevRef.current ?? '')) cb.current(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.enabled, a.dray, a.volume, a.fscrate, a.fsctype, a.fsctariff, a.deliveryzone, a.miles, adjustment]);

  return { adjustment };
}
